import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Redirect, Route, Switch, useRouteMatch } from "react-router";
import { KButton } from "@kyburz/kdesign-system";

import useSetState from "../../../hooks/useTypedSetState";
import BaseLayout from "../../../components/layouts/BaseLayout";
import CompaniesTab from "../../vehicleTemplates/components/tabs/CompaniesTab";
import { FirmwareType } from "../typings";
import FirmwareEditingTab from "./editing-tab/FirmwareEditingTab";
import { useStyles } from "./styles";

interface Props {
  title: string;
  initialFirmware: FirmwareType;
  handleSave?: (firmware: FirmwareType) => void;
  handleCancel?: () => void;
  isLoading?: boolean;
  isCreating?: boolean;
}

const DeviceFirmwareDetails: React.FC<Props> = ({
  title,
  initialFirmware,
  handleSave,
  handleCancel,
  isLoading,
  isCreating = false,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [fileName, setFileName] = useState("");

  const match = useRouteMatch<{ firmwareId?: string; tab?: string }>({
    path: "/config/devices/firmware/:firmwareId/:tab",
    strict: true,
    sensitive: true,
  });

  const { firmwareId, tab } = match?.params || {};

  const [{ activeTab }, setState] = useSetState({ activeTab: tab });

  const [firmwareData, setFirmwareData] = useSetState(initialFirmware);

  useEffect(() => {
    setState({ activeTab: tab });
  }, [setState, tab]);

  const handleTabClick = (_, newValue) => {
    history.push(`/config/devices/firmware/${firmwareId}/${newValue}`);
  };

  const onSave = () => {
    handleSave?.(firmwareData);
  };

  return (
    <BaseLayout
      title={title}
      handleArrowClick={handleCancel}
      activeTab={activeTab}
      tabs={[
        { label: "Firmware Editor", value: "editor" },
        { label: "Companies", value: "companies" },
      ]}
      handleTabChange={handleTabClick}
      mainClassname={classes.layout}
    >
      <div className={classes.main}>
        <Switch>
          <Route path="/config/devices/firmware/:firmwareId/editor" exact>
            <FirmwareEditingTab
              firmware={firmwareData}
              setFirmware={setFirmwareData}
              selectedFileName={fileName}
              setSelectedFileName={setFileName}
            />
          </Route>
          <Route path="/config/devices/firmware/:firmwareId/companies">
            <CompaniesTab
              title={`Device firmware ${firmwareData.version}`}
              selectedCompanies={firmwareData.allowed_customers || []}
              onCompaniesChange={(companies) =>
                setFirmwareData({ allowed_customers: companies })
              }
            />
          </Route>
          <Redirect to="/config/devices/firmware/:firmwareId/editor" />
        </Switch>
      </div>

      <footer className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={handleCancel} />

        <KButton
          children="Save and close"
          onClick={onSave}
          disabled={
            !firmwareData.version ||
            (isCreating && !firmwareData.contentsBase64) ||
            isLoading
          }
        />
      </footer>
    </BaseLayout>
  );
};

export default DeviceFirmwareDetails;
